import {filter} from '@prismicio/client';
import type {PrismicDocument} from '@prismicio/types';
import type {PinnedContentHeroSlice} from '@prismicio-types';
import usePrismicSearch from '~~/app/composables/content/usePrismicSearch';
import type {FilledContent} from '~~/app/composables/contentHelpers';
import {baseSearchPredicatesByCustomType} from '~~/app/utils/searchPredicates';

export type DocType = 'events' | 'articles' | 'guides' | 'successStories' | 'pressReleases';

export function resolveDocType(parentDoc: PrismicDocument | null | undefined, routeName: string) {
    const docType = parentDoc?.type;
    if (docType === 'homeArticles') return 'articles';
    if (docType === 'homeGuides') return 'guides';
    if (docType === 'homeSuccessStories') return 'successStories';
    if (docType === 'homePressReleases') return 'pressReleases';
    if (routeName === 'resources-events-page') return 'events';

    return null;
}

const SIDE_SLOT_COUNT = 3;

export function fallbackSlotCount(slice: PinnedContentHeroSlice): number {
    return slice.variation === 'multipleContent' ? 1 + SIDE_SLOT_COUNT : 1;
}

export function fallbackHeroContent(documents: PrismicDocument[] | null | undefined): FilledContent | null {
    return (documents?.[0] as FilledContent | undefined) ?? null;
}

export function fallbackSideContents(documents: PrismicDocument[] | null | undefined): FilledContent[] {
    if (!documents || documents.length <= 1) return [];
    return documents.slice(1, 1 + SIDE_SLOT_COUNT) as unknown as FilledContent[];
}

/**
 * Fetches fallback documents
 */
type PrismicSearchParams = Parameters<typeof usePrismicSearch>[0];

const pageTypeMap: Partial<Record<DocType, PrismicSearchParams>> = {
    articles: {type: 'article', ...baseSearchPredicatesByCustomType.article},
    guides: {type: 'guide', ...baseSearchPredicatesByCustomType.guide},
    successStories: {type: 'successStory', ...baseSearchPredicatesByCustomType.successStory},
    pressReleases: {type: 'pressRelease', ...baseSearchPredicatesByCustomType.pressRelease},
};

export function fetchFallbackDocuments(docType: DocType, count: number) {
    return async (): Promise<PrismicDocument[]> => {
        if (count < 1) return [];

        if (docType === 'events') {
            const yesterdayDate = new Date();
            yesterdayDate.setDate(yesterdayDate.getDate() - 1);
            const yesterday = yesterdayDate.toISOString().split('T')[0];
            const query = await usePrismicSearch(
                {
                    type: 'event',
                    predicates: [
                        ...(baseSearchPredicatesByCustomType.event.predicates ?? []),
                        filter.dateAfter('my.event.date', yesterday),
                        filter.any('my.event.eventType', ['offline', 'online']),
                    ],
                    orderings: [{field: 'my.event.date', direction: 'asc'}],
                    pageSize: count,
                    page: 1,
                },
                true,
            );
            return query.results;
        }

        const config = pageTypeMap[docType];
        if (!config) return [];

        const query = await usePrismicSearch({...config, pageSize: count, page: 1}, true);
        return query.results;
    };
}
